import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTradeShows } from "@/api/queries";
import { Button, Card, Select, Table } from "@/components/ui";

import { editionYears } from "./model";
import type { CardImportPreview } from "./queries";
import { useRunCardImport } from "./queries";

/**
 * The mapping step: one line per column of the seller's workbook, each with
 * the card field it feeds.
 *
 * The server proposes a mapping from the header names, which covers the old
 * CRM's template and most organisers' exports; anything it could not place is
 * left as *No importar* for the seller to decide. The three fields a card
 * cannot exist without must each come from a column, and no field may come
 * from two — both are said here, before *Importar*, and checked again by the
 * server.
 */
export function ImportMapping({
  importId,
  preview,
}: {
  importId: number;
  preview: CardImportPreview;
}) {
  const { t } = useTranslation();
  const [mapping, setMapping] = useState<Record<string, string>>(() => ({
    ...preview.suggested,
  }));
  const [tradeShow, setTradeShow] = useState("");
  const [edition, setEdition] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const run = useRunCardImport(importId);
  const tradeShows = useTradeShows();

  const labels = useMemo(
    () => new Map(preview.targets.map((target) => [target.key, target.label])),
    [preview.targets],
  );
  const chosen = Object.values(mapping).filter(Boolean);
  const repeated = new Set(chosen.filter((key, at) => chosen.indexOf(key) !== at));
  const missing = preview.targets.filter(
    (target) => target.required && !chosen.includes(target.key),
  );
  const mappedColumns = preview.headers
    .map((_, index) => index)
    .filter((index) => mapping[String(index)]);
  const ready = missing.length === 0 && repeated.size === 0;

  async function start() {
    setFailure(null);
    try {
      await run.mutateAsync({
        mapping: Object.fromEntries(Object.entries(mapping).filter(([, key]) => key)),
        trade_show: tradeShow ? Number(tradeShow) : null,
        edition: edition ? Number(edition) : null,
      });
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : t("errors.runImport"));
    }
  }

  return (
    <div className="grid gap-6">
      <Card title={t("cards.import.mappingTitle")}>
        <p className="mb-0 px-4 pt-3 text-sm text-mute-600">
          {t("cards.import.mappingHint", { count: preview.total_rows })}
        </p>
        <Table>
          <thead>
            <tr>
              <th>{t("cards.import.column")}</th>
              <th>{t("cards.import.examples")}</th>
              <th className="w-[280px]">{t("cards.import.field")}</th>
            </tr>
          </thead>
          <tbody>
            {preview.headers.map((header, index) => {
              const key = mapping[String(index)] ?? "";
              const examples = preview.samples
                .map((row) => row[index])
                .filter(Boolean)
                .slice(0, 3);
              return (
                <tr key={index}>
                  <td className="font-display font-extrabold">{header}</td>
                  <td className="text-xs text-mute-600">
                    {examples.length > 0
                      ? examples.join(" · ")
                      : t("cards.import.empty")}
                  </td>
                  <td>
                    <Select
                      aria-label={t("cards.import.fieldFor", { column: header })}
                      value={key}
                      aria-invalid={repeated.has(key) ? true : undefined}
                      className={repeated.has(key) ? "border-brand" : ""}
                      onChange={(event) =>
                        setMapping((previous) => ({
                          ...previous,
                          [String(index)]: event.target.value,
                        }))
                      }
                    >
                      <option value="">{t("cards.import.ignore")}</option>
                      {preview.targets.map((target) => (
                        <option key={target.key} value={target.key}>
                          {target.label}
                          {target.required ? " *" : ""}
                        </option>
                      ))}
                    </Select>
                    {repeated.has(key) ? (
                      <p className="mt-1 mb-0 text-xs text-brand-700">
                        {t("cards.import.repeated")}
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <Card title={t("cards.import.defaultsTitle")}>
        <div className="flex flex-wrap items-end gap-4 px-4 py-4">
          <label className="text-xs text-mute-600">
            {t("cards.fields.trade_show")}
            <Select
              className="mt-1 w-auto"
              value={tradeShow}
              onChange={(event) => setTradeShow(event.target.value)}
            >
              <option value="">{t("cards.form.noShow")}</option>
              {(tradeShows.data?.results ?? []).map((show) => (
                <option key={show.id} value={String(show.id)}>
                  {show.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-xs text-mute-600">
            {t("cards.fields.edition")}
            <Select
              className="mt-1 w-auto"
              value={edition}
              onChange={(event) => setEdition(event.target.value)}
            >
              <option value="">{t("cards.form.noEdition")}</option>
              {editionYears().map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </Select>
          </label>
          <p className="mb-0 max-w-[52ch] text-xs text-mute-600">
            {t("cards.import.defaultsHint")}
          </p>
        </div>
      </Card>

      {mappedColumns.length > 0 ? (
        <Card title={t("cards.import.previewTitle")}>
          <Table>
            <thead>
              <tr>
                {mappedColumns.map((index) => (
                  <th key={index}>{labels.get(mapping[String(index)] ?? "")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.samples.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {mappedColumns.map((index) => (
                    <td key={index} className="text-sm">
                      {row[index]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}

      {missing.length > 0 ? (
        <p role="alert" className="mb-0 border-2 border-brand px-3 py-2 text-sm">
          {t("cards.import.missing", {
            fields: missing.map((target) => target.label).join(", "),
          })}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="mb-0 border-2 border-brand px-3 py-2 text-sm">
          {failure}
        </p>
      ) : null}

      <div>
        <Button
          variant="primary"
          disabled={!ready || run.isPending}
          onClick={() => void start()}
        >
          {run.isPending
            ? t("cards.import.running")
            : t("cards.import.run", { count: preview.total_rows })}
        </Button>
      </div>
    </div>
  );
}
